import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiChevronLeft, mdiChevronRight } from '@mdi/js'
import { AppIcon } from '../ui/icon'
import { AssetTypeTile } from '../assets/AssetTypeTile'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet'
import { Button } from '../ui/button'
import { useAssetTypes, withoutArchivedTypes } from '../../hooks/useQueries'
import { useIsMobile } from '../../hooks/use-mobile'
import {
  type AssetTypeMeta,
  assetTypeLabel,
  buildPath,
  hasChildren,
  searchAtLevel,
  searchGlobal,
  topLevelTypes,
} from './asset-type-picker'

export interface AssetTypePickerProps {
  org: string
  /** Currently-selected asset type uid. Empty string means no selection. */
  value: string
  /** Called with the picked uid when the user selects a type. */
  onChange: (uid: string) => void
  /**
   * When set, restrict the picker to the descendants of the given asset type
   * (e.g. equipment root for AddEquipmentDialog). The first level shown is
   * that type's children; the breadcrumb uses it as the implicit root.
   */
  restrictToDescendantsOf?: string
  /**
   * When set, restrict the top-level list (and global-search results) to types
   * whose top-level ancestor is in this allow-list. Used by AddEquipmentDialog
   * to only offer trades that can be located under the chosen location.
   * `undefined` means "no restriction" (show everything).
   */
  allowedTopLevelUids?: string[]
  /**
   * Open the popover/sheet on mount. Used by AddEquipmentDialog when invoked
   * from a trade `+` button so the user lands directly on the trade's
   * sub-types instead of having to click the trigger first.
   */
  defaultOpen?: boolean
  /**
   * Called when the user closes the popover without picking (Escape, click
   * away). Not called when a pick closes it. Lets a click-to-edit row go back
   * to its resting text.
   */
  onDismiss?: () => void
  /** Trigger placeholder when no value is selected. */
  placeholder?: string
  /** Aria-label / title for the trigger button. */
  ariaLabel?: string
  disabled?: boolean
  /** Marks the default trigger `aria-invalid` (a required field left empty). */
  invalid?: boolean
  className?: string
  triggerTestId?: string
  /**
   * Custom trigger element (e.g. the asset's emoji in a detail-page header)
   * to use instead of the default combobox-style button. When provided, the
   * search/drill-down popover behavior is unchanged — only the trigger's
   * appearance changes.
   */
  customTrigger?: React.ReactNode
}

/**
 * Single picker UI that combines search and hierarchical drill-down.
 *
 * - Closed: shows the selected type with a breadcrumb path on the trigger.
 * - Open (no query): shows children of the current drill level. Each row with
 *   children has a right-side chevron that drills into that branch instead
 *   of selecting it. Clicking the row body selects the type at any depth.
 * - Open (with query): shows matches whose direct parent is the current drill
 *   level. If that's empty, falls back to a global search across the whole
 *   tree, with each result rendered with its breadcrumb path so the user
 *   knows where it sits.
 */
export function AssetTypePicker(props: AssetTypePickerProps) {
  const {
    org,
    value,
    onChange,
    restrictToDescendantsOf,
    allowedTopLevelUids,
    defaultOpen,
    onDismiss,
    placeholder,
    ariaLabel,
    disabled,
    invalid,
    className,
    triggerTestId,
    customTrigger,
  } = props
  const { t, i18n } = useTranslation('assets')
  const isMobile = useIsMobile()
  const { data: allAssetTypesRaw = [] } = useAssetTypes(org)
  // Memoized: a fresh array on every render re-fires the effects keyed on allTypes,
  // which reset the drill cursor and undid every chevron click.
  const allTypes = useMemo(
    () => withoutArchivedTypes(allAssetTypesRaw as unknown as (AssetTypeMeta & { archived_at?: string | null })[], value),
    [allAssetTypesRaw, value],
  )

  const [open, setOpen] = useState<boolean>(!!defaultOpen)
  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) onDismiss?.()
  }
  const [query, setQuery] = useState('')
  // Drill cursor: null means "top level". When restrictToDescendantsOf is set,
  // null still means top level — the helpers translate that into the children
  // of the restriction root.
  const [parentUid, setParentUid] = useState<string | null>(null)

  // When the picker opens with an existing value:
  //   - if the value has children, drill INTO them — common case is "+ Trade"
  //     in AddEquipmentDialog, where the user wants to refine the trade-level
  //     pre-selection into a leaf sub-type;
  //   - otherwise (leaf), seed at the parent's level so the user can switch
  //     to a sibling without leaving the surrounding context.
  useEffect(() => {
    if (!open) return
    setQuery('')
    if (!value) {
      setParentUid(null)
      return
    }
    if (hasChildren(allTypes, value)) {
      setParentUid(value)
      return
    }
    const path = buildPath(allTypes, value)
    if (path.length <= 1) {
      setParentUid(null)
      return
    }
    const parent = path[path.length - 2]
    // If the parent is the restriction root, treat as top-level.
    if (restrictToDescendantsOf && parent.uid === restrictToDescendantsOf) {
      setParentUid(null)
      return
    }
    setParentUid(parent.uid)
  }, [open, value, allTypes, restrictToDescendantsOf])

  // Breadcrumb chain root → … → current level (excluding the implicit root
  // when a restriction is set, since the restriction root is invisible).
  const breadcrumb = useMemo<AssetTypeMeta[]>(() => {
    if (!parentUid) return []
    const path = buildPath(allTypes, parentUid)
    if (restrictToDescendantsOf) {
      const idx = path.findIndex((p) => p.uid === restrictToDescendantsOf)
      if (idx >= 0) return path.slice(idx + 1)
    }
    return path
  }, [allTypes, parentUid, restrictToDescendantsOf])

  // Top-level allow-list (trade UIDs) — null means no restriction.
  const allowedSet = useMemo(
    () => (allowedTopLevelUids ? new Set(allowedTopLevelUids) : null),
    [allowedTopLevelUids],
  )

  // Items shown in the list. Memoised so cmdk doesn't re-run heavy work on
  // every keystroke.
  const localMatches = useMemo<AssetTypeMeta[]>(() => {
    let base: AssetTypeMeta[]
    if (query.trim() === '') {
      base = parentUid
        ? searchAtLevel(allTypes, parentUid, '', i18n.language)
        : topLevelTypes(allTypes, restrictToDescendantsOf)
    } else {
      base = parentUid
        ? searchAtLevel(allTypes, parentUid, query, i18n.language)
        : topLevelTypes(allTypes, restrictToDescendantsOf).filter((at) => {
            const label = assetTypeLabel(at, i18n.language).toLowerCase()
            const q = query.toLowerCase().trim()
            return label.includes(q) || at.code.toLowerCase().includes(q)
          })
    }
    // Restrict the trade list (top level only) to the allow-list. Once drilled
    // into an allowed trade, all its descendants inherit that scope.
    if (allowedSet && !parentUid) {
      base = base.filter((at) => allowedSet.has(at.uid))
    }
    return base
  }, [allTypes, parentUid, restrictToDescendantsOf, query, i18n.language, allowedSet])

  const globalMatches = useMemo(() => {
    if (query.trim() === '' || localMatches.length > 0) return []
    let matches = searchGlobal(allTypes, query, i18n.language, restrictToDescendantsOf)
    if (allowedSet) {
      // Keep matches whose top-level trade (first crumb, or the type itself
      // when it is a trade) is allowed.
      matches = matches.filter((m) => {
        const top = m.path.length > 0 ? m.path[0].uid : m.type.uid
        return allowedSet.has(top)
      })
    }
    return matches
  }, [allTypes, query, restrictToDescendantsOf, localMatches.length, i18n.language, allowedSet])

  const showGlobalFallback = query.trim() !== '' && localMatches.length === 0

  const handlePick = (uid: string) => {
    onChange(uid)
    setOpen(false)
  }

  const handleDrill = (uid: string) => {
    setParentUid(uid)
    setQuery('')
  }

  const handleBack = () => {
    if (breadcrumb.length === 0) return
    if (breadcrumb.length === 1) {
      setParentUid(null)
      return
    }
    setParentUid(breadcrumb[breadcrumb.length - 2].uid)
  }

  const handleCrumbClick = (idx: number) => {
    // idx -1 means root (i.e. clear parentUid). 0..n-2 means the corresponding
    // breadcrumb entry; n-1 is the current level so it's a no-op.
    if (idx < 0) {
      setParentUid(null)
      return
    }
    if (idx >= breadcrumb.length - 1) return
    setParentUid(breadcrumb[idx].uid)
  }

  // ---- Trigger label ----------------------------------------------------
  const selectedPath = useMemo(() => buildPath(allTypes, value), [allTypes, value])
  const triggerLabel = useMemo(() => {
    if (!value) return placeholder || t('asset_type_picker.placeholder')
    if (selectedPath.length === 0) return placeholder || t('asset_type_picker.placeholder')
    // Show the leaf label, with a muted breadcrumb suffix.
    return assetTypeLabel(selectedPath[selectedPath.length - 1], i18n.language)
  }, [value, selectedPath, i18n.language, placeholder, t])
  const triggerSuffix = useMemo(() => {
    if (!value || selectedPath.length <= 1) return ''
    let crumbs = selectedPath.slice(0, -1)
    if (restrictToDescendantsOf) {
      const idx = crumbs.findIndex((c) => c.uid === restrictToDescendantsOf)
      if (idx >= 0) crumbs = crumbs.slice(idx + 1)
    }
    if (crumbs.length === 0) return ''
    return crumbs.map((c) => assetTypeLabel(c, i18n.language)).join(' › ')
  }, [value, selectedPath, restrictToDescendantsOf, i18n.language])

  // ---- Content body shared between Popover and Sheet --------------------
  const renderContent = () => (
    <Command shouldFilter={false} className="rounded-md border bg-card">
      {(breadcrumb.length > 0 || restrictToDescendantsOf) && (
        <div
          className="flex items-center gap-1 px-2 py-1.5 border-b text-xs text-muted-foreground"
          data-testid="asset-type-picker-breadcrumb"
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={handleBack}
            disabled={breadcrumb.length === 0}
            aria-label={t('asset_type_picker.back')}
            title={t('asset_type_picker.back')}
            data-testid="asset-type-picker-back"
          >
            <AppIcon path={mdiChevronLeft} size="xs" tone="muted" />
          </Button>
          <button
            type="button"
            className="hover:text-foreground"
            onClick={() => handleCrumbClick(-1)}
            data-testid="asset-type-picker-crumb-root"
          >
            {restrictToDescendantsOf
              ? assetTypeLabel(
                  allTypes.find((at) => at.uid === restrictToDescendantsOf) ?? null,
                  i18n.language,
                ) || t('asset_type_picker.root')
              : t('asset_type_picker.root')}
          </button>
          {breadcrumb.map((crumb, idx) => (
            <span key={crumb.uid} className="flex items-center gap-1">
              <span aria-hidden>›</span>
              <button
                type="button"
                className={
                  idx === breadcrumb.length - 1
                    ? 'text-foreground font-medium cursor-default'
                    : 'hover:text-foreground'
                }
                onClick={() => handleCrumbClick(idx)}
                data-testid={`asset-type-picker-crumb-${crumb.code}`}
              >
                {assetTypeLabel(crumb, i18n.language)}
              </button>
            </span>
          ))}
        </div>
      )}
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder={t('asset_type_picker.search_placeholder')}
        aria-label={ariaLabel || t('asset_type_picker.search_aria_label')}
        data-testid="asset-type-picker-input"
      />
      <CommandList>
        {!showGlobalFallback && localMatches.length === 0 && (
          <CommandEmpty>{t('asset_type_picker.empty')}</CommandEmpty>
        )}
        {!showGlobalFallback && localMatches.length > 0 && (
          <CommandGroup>
            {localMatches.map((item) => {
              const label = assetTypeLabel(item, i18n.language)
              const drillable = hasChildren(allTypes, item.uid)
              return (
                <CommandItem
                  key={item.uid}
                  value={`${label} ${item.code}`}
                  onSelect={() => handlePick(item.uid)}
                  data-testid={`asset-type-picker-item-${item.code}`}
                  className="group"
                >
                  <span className="flex items-center gap-2 flex-1 min-w-0">
                    <AssetTypeTile type={item} size="sm" />
                    <span className="truncate">{label}</span>
                  </span>
                  {drillable && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        handleDrill(item.uid)
                      }}
                      onMouseDown={(e) => {
                        // Prevent cmdk from registering the click as a row select.
                        e.stopPropagation()
                      }}
                      className="ml-2 inline-flex h-6 w-6 items-center justify-center rounded hover:bg-accent text-muted-foreground hover:text-foreground"
                      aria-label={t('asset_type_picker.drill_in', { type: label })}
                      title={t('asset_type_picker.drill_in', { type: label })}
                      data-testid={`asset-type-picker-drill-${item.code}`}
                    >
                      <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
                    </button>
                  )}
                </CommandItem>
              )
            })}
          </CommandGroup>
        )}
        {showGlobalFallback && globalMatches.length === 0 && (
          <CommandEmpty>{t('asset_type_picker.empty')}</CommandEmpty>
        )}
        {showGlobalFallback && globalMatches.length > 0 && (
          <CommandGroup heading={t('asset_type_picker.global_match_hint')}>
            {globalMatches.map((match) => {
              const label = assetTypeLabel(match.type, i18n.language)
              const crumbStr = match.path.map((p) => assetTypeLabel(p, i18n.language)).join(' › ')
              return (
                <CommandItem
                  key={match.type.uid}
                  value={`${crumbStr} ${label} ${match.type.code}`}
                  onSelect={() => handlePick(match.type.uid)}
                  data-testid={`asset-type-picker-global-${match.type.code}`}
                >
                  <span className="flex items-center gap-2 flex-1 min-w-0">
                    <AssetTypeTile type={match.type} size="sm" />
                    <span className="min-w-0">
                      <span className="truncate block">{label}</span>
                      {crumbStr && (
                        <span className="block text-xs text-muted-foreground truncate">{crumbStr}</span>
                      )}
                    </span>
                  </span>
                </CommandItem>
              )
            })}
          </CommandGroup>
        )}
      </CommandList>
    </Command>
  )

  const trigger = (
    <Button
      type="button"
      variant="outline"
      role="combobox"
      aria-expanded={open}
      aria-label={ariaLabel || t('asset_type_picker.search_aria_label')}
      title={ariaLabel || t('asset_type_picker.search_aria_label')}
      disabled={disabled}
      aria-invalid={invalid}
      className={`w-full justify-between font-normal ${className ?? ''}`}
      data-testid={triggerTestId || 'asset-type-picker-trigger'}
    >
      <span className="flex flex-col items-start min-w-0 text-left">
        <span className="truncate w-full">{triggerLabel}</span>
        {triggerSuffix && (
          <span className="text-xs text-muted-foreground truncate w-full">{triggerSuffix}</span>
        )}
      </span>
      <AppIcon path={mdiChevronDown} size="sm" tone="muted" className="ml-2 opacity-50 shrink-0" />
    </Button>
  )
  const triggerNode = customTrigger ?? trigger

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetTrigger asChild>{triggerNode}</SheetTrigger>
        <SheetContent side="bottom" className="h-[75vh] p-0" data-testid="asset-type-picker-drawer">
          <SheetHeader className="px-3 py-2 border-b">
            <SheetTitle>{ariaLabel || t('asset_type_picker.search_aria_label')}</SheetTitle>
          </SheetHeader>
          <div className="h-full overflow-y-auto">{renderContent()}</div>
        </SheetContent>
      </Sheet>
    )
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{triggerNode}</PopoverTrigger>
      <PopoverContent className="w-[420px] p-0" align="start" data-testid="asset-type-picker-popover">
        {renderContent()}
      </PopoverContent>
    </Popover>
  )
}
